// src/pages/AdminLoginLogs.tsx
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  RefreshCw,
  ArrowLeft,
  Activity,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Search,
  MapPin,
  User,
  Loader2,
  Shield,
  Clock
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { loginLogsAPI } from '@/services/api';

interface LoginLog {
  id: number;
  username: string;
  ipAddress: string;
  userAgent: string;
  status: string;
  statusMessage: string;
  failureReason?: string | null;
  loginTimestamp: string;
}

export default function AdminLoginLogs() {
  const [logs, setLogs] = useState<LoginLog[]>([]);
  const [filteredLogs, setFilteredLogs] = useState<LoginLog[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'ALL' | 'SUCCESS' | 'FAILED'>('ALL');
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  const { toast } = useToast();
  const navigate = useNavigate();

  const fetchLogs = async (pageNum = 0, refresh = false) => {
    try {
      if (refresh) setIsRefreshing(true);
      else setIsLoading(true);

      const response = await loginLogsAPI.getAll(pageNum, 20);
      
      if (pageNum === 0) {
        setLogs(response.content || []);
      } else {
        setLogs(prev => [...prev, ...(response.content || [])]);
      }

      setHasMore(!response.last);
      setPage(pageNum);
    } catch (error: any) {
      console.error('Failed to fetch logs:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to load login logs',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    fetchLogs(0);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      fetchLogs(0, true);
    }, 10000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    let filtered = logs;

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(log =>
        log.username.toLowerCase().includes(query) ||
        log.ipAddress.includes(query)
      );
    }

    if (statusFilter === 'SUCCESS') {
      filtered = filtered.filter(log => log.status === 'SUCCESS');
    } else if (statusFilter === 'FAILED') {
      filtered = filtered.filter(log => log.status !== 'SUCCESS');
    }

    setFilteredLogs(filtered);
  }, [logs, searchQuery, statusFilter]);

  const formatDate = (dateStr: string) => {
    try {
      const date = new Date(dateStr);
      return date.toLocaleString('en-KE', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true,
        timeZone: 'Africa/Nairobi'
      });
    } catch {
      return 'Invalid date';
    }
  };

  const getRelativeTime = (dateStr: string) => {
    try {
      const now = new Date();
      const past = new Date(dateStr);
      const diffMs = now.getTime() - past.getTime();
      
      if (diffMs < 0) return 'Just now';
      
      const diffMins = Math.floor(diffMs / 60000);
      if (diffMins < 1) return 'Just now';
      if (diffMins < 60) return `${diffMins}m ago`;
      
      const diffHours = Math.floor(diffMins / 60);
      if (diffHours < 24) return `${diffHours}h ago`;
      
      const diffDays = Math.floor(diffHours / 24);
      return `${diffDays}d ago`;
    } catch {
      return 'Unknown';
    }
  };

  const statusConfig: Record<string, any> = {
    SUCCESS: {
      icon: CheckCircle,
      color: 'text-green-600',
      bg: 'bg-green-50 dark:bg-green-900/20',
      border: 'border-green-200 dark:border-green-800',
      label: 'Success'
    },
    FAILED_INVALID_CREDENTIALS: {
      icon: XCircle,
      color: 'text-red-600',
      bg: 'bg-red-50 dark:bg-red-900/20',
      border: 'border-red-200 dark:border-red-800',
      label: 'Failed'
    },
    FAILED_ACCOUNT_LOCKED: {
      icon: AlertTriangle,
      color: 'text-orange-600',
      bg: 'bg-orange-50 dark:bg-orange-900/20',
      border: 'border-orange-200 dark:border-orange-800',
      label: 'Locked'
    },
    FAILED_ACCOUNT_DISABLED: {
      icon: XCircle,
      color: 'text-red-600',
      bg: 'bg-red-50 dark:bg-red-900/20',
      border: 'border-red-200 dark:border-red-800',
      label: 'Disabled'
    },
  };

  const failedCount = logs.filter(l => l.status !== 'SUCCESS').length;

  if (isLoading && logs.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center space-y-4">
          <Loader2 className="w-8 h-8 animate-spin text-teal-500 mx-auto" />
          <p className="text-gray-600 dark:text-gray-400">Loading login logs...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pb-6">
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 sticky top-0 z-40 shadow-sm">
        <div className="px-4 py-3">
          <div className="flex items-center gap-3 mb-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/admin/dashboard')}
              className="p-2"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex-1">
              <h1 className="font-bold text-lg text-gray-900 dark:text-white flex items-center gap-2">
                <Activity className="w-5 h-5 text-teal-500" />
                Login Activity
              </h1>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Monitor all admin login attempts
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => fetchLogs(0, true)}
              disabled={isRefreshing}
              className="relative"
            >
              <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
              {isRefreshing && (
                <span className="absolute -top-1 -right-1 w-2 h-2 bg-teal-500 rounded-full animate-pulse" />
              )}
            </Button>
          </div>

          <div className="grid grid-cols-2 gap-2 mb-3">
            <Card className="border-l-4 border-l-teal-500">
              <CardContent className="p-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-600 dark:text-gray-400">Total Logs</p>
                    <p className="text-xl font-bold text-gray-900 dark:text-white">{logs.length}</p>
                  </div>
                  <Shield className="w-6 h-6 text-teal-500" />
                </div>
              </CardContent>
            </Card>

            <Card className={`border-l-4 ${failedCount > 0 ? 'border-l-red-500' : 'border-l-gray-300'}`}>
              <CardContent className="p-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-600 dark:text-gray-400">Failed Attempts</p>
                    <p className="text-xl font-bold text-gray-900 dark:text-white">{failedCount}</p>
                  </div>
                  <AlertTriangle className={`w-6 h-6 ${failedCount > 0 ? 'text-red-500 animate-pulse' : 'text-gray-300'}`} />
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="space-y-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search by username or IP..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>

            <Select value={statusFilter} onValueChange={(val: any) => setStatusFilter(val)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All Logs ({logs.length})</SelectItem>
                <SelectItem value="SUCCESS">
                  Success Only ({logs.filter(l => l.status === 'SUCCESS').length})
                </SelectItem>
                <SelectItem value="FAILED">
                  Failed Only ({failedCount})
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <div className="px-4 py-4 space-y-3">
        {filteredLogs.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center">
              <Activity className="w-12 h-12 text-gray-300 mx-auto mb-2" />
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {searchQuery || statusFilter !== 'ALL' 
                  ? 'No logs match your filters'
                  : 'No login logs yet'}
              </p>
            </CardContent>
          </Card>
        ) : (
          filteredLogs.map((log) => {
            const config = statusConfig[log.status] || statusConfig.FAILED_INVALID_CREDENTIALS;
            const StatusIcon = config.icon;

            return (
              <Card key={log.id} className={`border-l-4 ${config.border}`}>
                <CardContent className="p-4 space-y-3">
                  <div className="flex items-start justify-between">
                    <Badge className={`${config.bg} ${config.color} border ${config.border}`}>
                      <StatusIcon className="w-3 h-3 mr-1" />
                      {config.label}
                    </Badge>
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {getRelativeTime(log.loginTimestamp)}
                    </span>
                  </div>

                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-gray-400" />
                    <span className="font-medium text-gray-900 dark:text-white">
                      {log.username}
                    </span>
                  </div>

                  <div className="flex items-center gap-2 text-sm">
                    <MapPin className="w-4 h-4 text-gray-400" />
                    <span className="font-mono text-gray-600 dark:text-gray-400">
                      {log.ipAddress}
                    </span>
                  </div>

                  <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                    <Clock className="w-3 h-3" />
                    {formatDate(log.loginTimestamp)}
                  </div>

                  {log.status !== 'SUCCESS' && log.failureReason && (
                    <div className={`${config.bg} border ${config.border} rounded-lg p-3`}>
                      <div className="flex items-start gap-2">
                        <AlertTriangle className={`w-4 h-4 ${config.color} mt-0.5 flex-shrink-0`} />
                        <div>
                          <p className={`text-xs font-medium ${config.color}`}>
                            Failure Reason:
                          </p>
                          <p className={`text-sm ${config.color} mt-1`}>
                            {log.failureReason}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {log.userAgent && (
                    <details className="text-xs text-gray-500 dark:text-gray-400">
                      <summary className="cursor-pointer hover:text-gray-700 dark:hover:text-gray-300">
                        Device Info
                      </summary>
                      <p className="mt-2 font-mono text-xs bg-gray-50 dark:bg-gray-800 p-2 rounded">
                        {log.userAgent}
                      </p>
                    </details>
                  )}
                </CardContent>
              </Card>
            );
          })
        )}

        {hasMore && filteredLogs.length > 0 && (
          <Button
            variant="outline"
            className="w-full"
            onClick={() => fetchLogs(page + 1)}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Loading...
              </>
            ) : (
              'Load More'
            )}
          </Button>
        )}
      </div>

      {isRefreshing && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-50">
          <div className="bg-teal-500 text-white px-4 py-2 rounded-full shadow-lg flex items-center gap-2 animate-in slide-in-from-top">
            <RefreshCw className="w-4 h-4 animate-spin" />
            <span className="text-sm font-medium">Refreshing...</span>
          </div>
        </div>
      )}
    </div>
  );
}